import {
	BINDINGS,
	BINDINGS_AI_SEARCH,
	BINDINGS_DURABLE_OBJECTS,
	BINDINGS_KV,
	BINDINGS_QUEUES,
	BINDINGS_R2,
	BINDINGS_SERVICES,
	BINDINGS_VECTORIZE,
	BINDINGS_VERSION_METADATA,
} from '@federa/const/bindings';
import { BINDINGS_SECRETS } from '@federa/const/bindings-secrets';
import { BINDINGS_DO_ROOMS } from '@federa/const/bindings-do-rooms';
import { ENV_ENVIRONMENT } from '@federa/const/env-var-names';
import { ownValueOf } from '@federa/const/own-key-lookup';
import type { BindingManifest } from '@federa/types/api-env';
import type { EnvironmentType } from '@federa/types/wrangler-deploy-env';

export const ENVIRONMENTS = {
	DEVELOPMENT: 'development',
	PRODUCTION: 'production',
} as const satisfies Record<string, EnvironmentType>;

/** The wrangler `env.<key>` block keys. Non-prod is `dev`, not `development`, and `wrangler dev --env development` crashes: use this, never `ENVIRONMENTS`. */
export const WRANGLER_ENV_KEYS = {
	DEV: 'dev',
	PRODUCTION: 'production',
} as const;

export type WranglerEnvKey = (typeof WRANGLER_ENV_KEYS)[keyof typeof WRANGLER_ENV_KEYS];

/**
 * The env block a page or render that knows only "am I production?" reads a per-environment record by.
 * @runtime-derived-ssot-helper the one spelling of that choice; each respelling picks its own direction, and a flipped
 * one hands a production page a development origin.
 */
export function wranglerEnvKeyFor(isProduction: boolean): WranglerEnvKey {
	return isProduction ? WRANGLER_ENV_KEYS.PRODUCTION : WRANGLER_ENV_KEYS.DEV;
}

/** Deployment label -> wrangler `env.<key>` object key, the map an operator-facing environment argument resolves
 *  through. Wrangler-facing `--env` values MUST come from WRANGLER_ENV_KEYS (no `env.development` block exists);
 *  wrangler keys map to themselves. `satisfies`, never the wide annotation (`QUAL-CONST-RECORD-WIDE-KEY-001`): the
 *  accepted labels stay the declared key set, and an operator-typed label is looked up through
 *  {@link wranglerEnvKeyForLabel}, the one place the key set is widened. */
export const DEPLOYMENT_ENV_TO_WRANGLER_KEY = {
	[ENVIRONMENTS.DEVELOPMENT]: WRANGLER_ENV_KEYS.DEV,
	[ENVIRONMENTS.PRODUCTION]: WRANGLER_ENV_KEYS.PRODUCTION,
	[WRANGLER_ENV_KEYS.DEV]: WRANGLER_ENV_KEYS.DEV,
} as const satisfies Readonly<Record<string, WranglerEnvKey>>;

/**
 * The wrangler block an operator-typed deployment label names, or undefined when the map declares no such label.
 * OWN KEYS ONLY: a label such as `toString` names an inherited member, never a block, so a bare index would hand the
 * caller a function where it expects an env key.
 * @runtime-derived-ssot-helper: the one lookup over the same-leaf {@link DEPLOYMENT_ENV_TO_WRANGLER_KEY}.
 */
export function wranglerEnvKeyForLabel(label: string): WranglerEnvKey | undefined {
	const byLabel: Readonly<Record<string, WranglerEnvKey>> = DEPLOYMENT_ENV_TO_WRANGLER_KEY;
	return ownValueOf(byLabel, label);
}

export const DEFAULT_TIMEZONE = 'America/New_York';

/** Binding manifest for the main storage-gateway worker. */
export const STORAGE_GATEWAY_BINDINGS: BindingManifest = {
	required: [
		BINDINGS.FEDERA,
		BINDINGS.KAIZEN,
		// No BINDINGS.ACTIVITIES: nothing this worker registers reads it, and the worker declares no ACTIVITIES wrangler
		// binding. The `activities` database and its rows stay in place, dormant.
		BINDINGS.FUNCTIONS,
		// No KNOWLEDGE member: nothing reads one, and no wrangler config declares a D1 binding for the knowledge-{dev,prod}
		// pair -- the knowledgebase corpus lives on the groups D1 + the AI Search instance.
		BINDINGS.CONVERSATIONS_DB,
		// CONTACTS_DB, REQUIRED since the 2026-09-04 contacts-prod provisioning back-filled
		// `CF_RESOURCE_IDS.D1.CONTACTS_DB.prod` and the wrangler rows went dev-and-prod (this is the promotion the
		// optional-tier rationale it replaces named as its own end state). The demand is transitive in-process
		// dispatch: this entry registers `userProvisionFlow`, whose closure reaches `contacts-resolve-on-join`
		// through `user-profile-find-or-create`, and that leaf runs `executeD1Crud` against `BINDINGS.CONTACTS`
		// with no degrade arm -- absent, the node throws rather than narrowing a capability.
		BINDINGS.CONTACTS,
		// BINDINGS.PRODIGY RE-ADDED 2026-08-21 with the worker's PRODIGY_DATABASE wrangler binding. The removal
		// rationale it replaces measured DIRECT registry membership ("registered exclusively on
		// microservices/assistant") where the demand is a CLOSURE property: this entry registers `save-message`, whose
		// walked closure is save-message -> assistant-agents-run -> assistant-agent-run -> activity-summary, and that
		// leaf's `update-d1` node binds BINDINGS.PRODIGY on a d1-crud write with no degrade arm. REQUIRED for the same
		// reason FUNCTIONS is: absent, the node throws rather than narrowing a capability.
		BINDINGS.PRODIGY,
		BINDINGS_R2.FILES,
		BINDINGS_DURABLE_OBJECTS.USER,
		BINDINGS_DURABLE_OBJECTS.PROPERTY,
		// No BINDINGS_AI.AI: no flow this worker registers reads Workers AI, and the worker declares no `ai` wrangler block.
		// AI Search namespace. No flow this worker registers reads it -- the binding is retained only until its manifest
		// cut lands as its own change (filed; a binding no registered flow reads is inventory, not a narrowed surface).
		BINDINGS_AI_SEARCH.KNOWLEDGE,
		ENV_ENVIRONMENT,
	],
	// NO FLOW_RUNNER binding: the PropertyDO/UserDO flows run IN-PROCESS. Do NOT list it even as optional -- that logs a false "Missing required bindings" on every request.
	// GUEST_SESSION_SECRET is OPTIONAL rather than required, and the distinction is load-bearing: absent, the guest arm of
	// the `/user/*` gate issues nothing and verifies nothing (guests lose the socket, nobody gets an ungated one), which is
	// a degraded feature, not a broken worker. Listing it as required would log "Missing required bindings" on every
	// request of a deployment that legitimately runs without guest sessions.
	optional: [
		BINDINGS_VECTORIZE.SHARED,
		BINDINGS_SECRETS.GUEST_SESSION_SECRET,
		// OPTIONAL for the same fail-closed reason: an unprovisioned caller secret cannot be matched, so that caller is
		// denied. A worker running with neither is a worker no machine caller can reach -- degraded, not broken.
		BINDINGS_SECRETS.ZOHO_CALLER_SECRET,
		BINDINGS_SECRETS.CLI_CALLER_SECRET,
		BINDINGS_SECRETS.APP_CALLER_SECRET,
		BINDINGS_SECRETS.CLOUDFLARE_ACCOUNT_ID,
		// The worker's own wrangler.jsonc declares each of these, so the manifest has to CARRY them
		// (`QUAL-WORKER-WRANGLER-BINDING-UNMANIFESTED-001`): a declared-but-unmanifested binding is a resource the
		// deploy provisions and the startup validator never checks. OPTIONAL rather than required, per binding:
		//  - the two KV namespaces and the RAG bucket are CACHES / corpora -- absent, a read misses and the flow
		//    recomputes or answers empty, which is degraded, never broken;
		//  - the queue is the ASYNC dispatch lane; without it the synchronous path still answers;
		//  - the two service bindings are cross-worker forwards whose callers already answer a shaped error when the
		//    forward fails, so listing them required would log "Missing required bindings" on every request of a
		//    deployment that legitimately runs without one.
		BINDINGS_KV.CACHE,
		BINDINGS_KV.RECENT_PUBLISHED_PROPERTIES,
		BINDINGS_R2.PROPERTY_RAG,
		BINDINGS_QUEUES.AGENTS_SDK_RUNNER,
		BINDINGS_SERVICES.ASSISTANT,
		// Same duty as the block above: this worker's own wrangler.jsonc declares `version_metadata`, so an unmanifested
		// entry is a resource the deploy provisions and the startup validator never checks
		// (`QUAL-WORKER-WRANGLER-BINDING-UNMANIFESTED-001`). OPTIONAL on exactly the grounds the money manifests state
		// (NAVIGATOR_BINDINGS, `const/worker-env-manifests-mfe.ts`): it is the version half of every ledger seal's
		// posting stamp, and `resolveLedgerPostingStamp` falls back to a caller-supplied version id, so a posting still
		// LANDS rather than throwing. What is lost is the one value in that stamp the audited party cannot choose --
		// which is why it belongs in a tier the startup line names rather than nowhere at all.
		BINDINGS_VERSION_METADATA.CF_VERSION_METADATA,
		// NOTIFICATION_CHANNEL replaces the NOTIFICATIONS_SERVICE row this list carried. The four notify leaves on this
		// worker (add-participant, auction-outcome, property-auction-timer-tick, property-offers-void-previous) now run
		// `notifications-send` IN-PROCESS against BINDINGS.FEDERA instead of POSTing the notifications worker, so the
		// only cross-script demand left is the per-user channel the flow's broadcast step pokes. OPTIONAL for the tier
		// reason above and this one: `broadcastNotificationsFromDo` logs and returns on an absent namespace, so the D1
		// row still lands and only the live badge is lost.
		BINDINGS_DURABLE_OBJECTS.NOTIFICATION_CHANNEL,
		// Same duty, same tier, for the two DO namespaces the worker's own `durable_objects.bindings` declares beyond
		// USER and PROPERTY. OPTIONAL rather than required, per binding:
		//  - FEED_DURABLE_OBJECT is the auction-feed BROADCAST hub: absent, a live-feed delta is not fanned out and the
		//    client falls back to its poll, which is degraded, never broken -- D1 stays the record store either way;
		//  - `rooms` is a CROSS-SCRIPT binding to ChatRoom on the chats service, so it resolves only where that script
		//    is deployed; requiring it would log "Missing required bindings" on every request of a deployment that has
		//    no chats peer. Its failure mode when a create-room run DOES need it is documented at the binding itself in
		//    `microservices/storage-gateway/wrangler.jsonc`, and that is a runtime contract this startup tier cannot
		//    tighten without breaking the peerless deployment.
		BINDINGS_DURABLE_OBJECTS.FEED,
		BINDINGS_DO_ROOMS.ROOMS,
	],
} as const;
